/**
 * The Transaction Centre.
 *
 * The last `planned` destination on this rail, and the one that waited for a
 * table: `ondo.registration_fees` did not ship with the transfer because "a fee
 * row with no screen and no provider behind it asserts that money is owed with
 * nothing able to say whether it moved". 20260910110000 is the row; this is the
 * screen; and the provider half is answered the way settlements answered it —
 * the firm RECORDS that it paid, and ONDO takes nothing.
 *
 * The other two things a sale produces have screens already, so they are
 * counts here that link out — see `toTransactionsView`.
 */
export const TRANSACTIONS_COPY = {
  loading: "Reading your ledger…",
  errorTitle: "Can’t load the Transaction Centre",

  lead: "What a sale produces, in one place.",
  leadDetail:
    "A transfer waits on its buyer on Sold Works, a settlement outstanding is on Resale settlements, and the registration fee each sale owes is listed here. ONDO invoices the fee and records your word that it was paid; it takes no money.",

  tiles: {
    awaitingBuyer: "Awaiting a buyer",
    awaitingBuyerNote: "Sales whose title has not landed",
    feesDue: "Fees due",
    feesDueNote: "Registration fees not yet recorded as paid",
    settlementsDue: "Settlements due",
    settlementsDueNote: "Owed under your covenants",
    open: "Open",
  },

  /** '$500 in fees due'. */
  feesDueSummary: (total: string) => `${total} in fees due`,

  groups: {
    due: "Fees due",
    paid: "Recorded as paid",
    void: "Sale withdrawn",
  },

  noFees: "No sale has been recorded yet, so no fee is owed.",

  tier: {
    plain: "Base",
    premium: "Premium · covenant attached",
  },
  state: {
    due: "Due",
    paid: "Recorded as paid",
    void: "Void",
  },
  soldOn: "sold",
  recordedPaid: "Recorded as paid",
  by: "by",
  voidNote: "The sale was withdrawn, and with it the fee. Nothing is owed under it.",

  /**
   * The control, and the sentence beside it, for a director.
   *
   * "Record as paid" and never "Pay": nothing here moves money, and a button
   * that read as a payment would be describing a provider this registry does
   * not have. The note is optional and is the invoice reference in practice.
   */
  actions: {
    record: "Record as paid",
    confirm: "Yes, record it",
    cancel: "Not yet",
    notePlaceholder: "Invoice or reference (optional)",
    recordNote:
      "This records the firm’s word that the fee was paid, with your name and the date. It cannot be undone; a mistake is a note on the row.",
  },

  /** Shown in place of the control, to a seat below admin. */
  needsAdmin:
    "Recording a fee as paid commits the gallery, so it is a director’s to do. What is owed is shown here for everyone.",

  errors: {
    refused: "This account cannot read the gallery’s ledger.",
  },
} as const;

/**
 * The billing contact panel — OND-93.
 *
 * ON THE TRANSACTION CENTRE AND NOT ON NOTIFICATION SETTINGS, which is the
 * placement decision worth recording where somebody will read it. The
 * notification settings screen chooses how a firm's INBOX behaves, and a fee
 * notice is not a notification: it is an email to one person, about money, and
 * the ledger it describes is on this screen. A control on the settings screen
 * would have suggested that silencing a kind could silence an invoice.
 *
 * THE WORDS AVOID "BILL" AND "ACCOUNT" DELIBERATELY. ONDO holds no account and
 * takes no money — OND-51 is deferred — so the panel says who is TOLD, not who
 * is charged.
 */
export const BILLING_CONTACT_COPY = {
  title: "Billing contact",
  lead: "Who at this firm receives the ONDO fee notices, and the monthly statement and invoice.",

  loading: "Reading who is told…",

  /** Shown when the firm has never chosen. */
  isDefault: "This is the default — your longest-standing administrator. Nobody has chosen otherwise.",
  isChosen: "Chosen by this firm.",

  /** When a firm has no active administrator at all, so nobody can be told. */
  nobody:
    "Nobody at this firm can be told: there is no active administrator to default to. The fees are still recorded and listed above.",

  chooseLabel: "Send them to",
  keepDefault: "The default administrator",
  save: "Change",
  saving: "Changing…",

  /** Shown in place of the control, to a seat below admin. */
  needsAdmin:
    "Choosing who receives the fee notices commits the firm, so it is a director’s to do. Who receives them is shown here for everyone.",

  /** The honest note under the control. */
  note: "Fee notices and the monthly statement are emailed. ONDO takes no money and does not observe payments; a director records a fee as paid above.",

  errors: {
    refused: "This account cannot change who the gallery’s fee notices go to.",
    noSeat: "That ONDO id does not hold a seat at this firm.",
  },
} as const;
